import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const DEFAULT_EXPORT_FILENAME_BASE = 'report';

const BC01_SHEET_NAME = 'Tong_hop';

/**
 * Tạo tên file Excel theo định dạng `${base}_${YYYYMMDD}.xlsx`.
 * `base` thường là chuỗi i18n không dấu (vd. `Bao_cao_tong_hop_theo_ngay`).
 */
export function buildReportExcelFileName(base: string): string {
  const safeBase = (base ?? '').trim() || DEFAULT_EXPORT_FILENAME_BASE;
  return `${safeBase}_${dayjs().format('YYYYMMDD')}.xlsx`;
}

const COL_WCH_PADDING = 4;

const COL_WCH_MAX = 90;

function padRow(row: string[], colCount: number): string[] {
  return Array.from({ length: colCount }, (_, i) => (row[i] != null ? String(row[i]) : ''));
}

export function computeColumnWchFromAoa(aoa: string[][]): XLSX.ColInfo[] {
  if (aoa.length === 0) {
    return [];
  }
  const colCount = Math.max(...aoa.map((r) => r.length), 0);
  if (colCount === 0) {
    return [];
  }
  const maxLens = new Array<number>(colCount).fill(0);
  for (const row of aoa) {
    for (let c = 0; c < colCount; c++) {
      const raw = row[c];
      const s = raw != null ? String(raw) : '';
      const len = s.length;
      if (len > maxLens[c]) {
        maxLens[c] = len;
      }
    }
  }
  return maxLens.map((maxLen) => {
    const wch = Math.min(COL_WCH_MAX, maxLen + COL_WCH_PADDING);
    return { wch: Math.max(wch, COL_WCH_PADDING + 2) };
  });
}

export function buildBc01ExcelAoa(headers: string[], data: string[][]): string[][] {
  const colCount = Math.max(
    headers.length,
    ...data.map((r) => r.length),
    0
  );
  if (colCount === 0) {
    return [];
  }
  const headerRow = padRow(headers, colCount);
  const spacerRow = Array.from<string>({ length: colCount }).fill('');
  const bodyRows = data.map((row) => padRow(row, colCount));
  return [headerRow, spacerRow, ...bodyRows];
}

export type DownloadBcReportExcelOptions = {
  fileName?: string;
  /** Tên sheet Excel (tối đa 31 ký tự). */
  sheetName?: string;
};

export function downloadBc01ReportExcel(
  headers: string[],
  data: string[][],
  options?: DownloadBcReportExcelOptions
): void {
  const aoa = buildBc01ExcelAoa(headers, data);
  if (aoa.length === 0) {
    return;
  }
  const fileName = options?.fileName ?? buildReportExcelFileName(DEFAULT_EXPORT_FILENAME_BASE);
  const rawSheet = options?.sheetName ?? BC01_SHEET_NAME;
  const sheetName = rawSheet.length > 31 ? rawSheet.slice(0, 31) : rawSheet;

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet['!cols'] = computeColumnWchFromAoa(aoa);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, fileName);
}
